import {Box, Stack, Typography} from '@mui/material';
import {AlertInfo} from 'presentation/components';
import {CopySectionMaskEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import {useContractStatus} from 'presentation/hooks';
import React, {Fragment} from 'react';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {HelpPageTaggingEnum} from 'main/analytics/gaTagsEnum';
import {CHANNELS_CONTACTS} from 'presentation/pages/private/help/constants/channelsContacts';
import {ChannelAccordion} from 'presentation/pages/private/help/components/ContactChannels/components/ChannelAccordion';
import {ChannelPhone} from 'presentation/pages/private/help/components/ContactChannels/components/ChannelPhone';
import {ChannelRegion} from 'presentation/pages/private/help/components/ContactChannels/components/ChannelRegion';
import {FinancialSupportAccordion} from 'presentation/pages/private/help/components/ContactChannels/components/FinancialSupportAccordion';
import {OmbudsmanFormLink} from 'presentation/pages/private/help/components/ContactChannels/components/OmbudsmanFormLink';
import {ContactChannelsBrandProps} from 'presentation/pages/private/help/components/ContactChannels/types';

const {
	title,
	callCenter,
	healthExamsAppointments,
	SAC,
	ombudsman,
	cancellation,
	sales,
	financialSupport,
} = translations['pt-br'].privateFlow.help.channels.hap;

export function HapvidaContactChannels({
	expandedState,
	handleAccordionToggle,
}: ContactChannelsBrandProps) {
	const {isValid} = useContractStatus();

	const {isResponsibleWithoutBenefits, healthOnly} = useAccountInformation();
	return (
		<Stack gap={1}>
			<Typography variant="subheader">{title}</Typography>

			<Box>
				{isValid ? (
					<ChannelAccordion
						dataTagId={HelpPageTaggingEnum.CLICK_EXPANDIR_TELEFONE_CALL_CENTER_AJUDA}
						title={callCenter.title}
						handleAccordionToggle={handleAccordionToggle}
						expandedState={expandedState}>
						<Stack gap={1}>
							<AlertInfo.Root>{callCenter.alert}</AlertInfo.Root>
							<ChannelPhone
								value={CHANNELS_CONTACTS.HAPVIDA.CALL_CENTER.phone1}
								mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
							/>
							<ChannelPhone
								value={CHANNELS_CONTACTS.HAPVIDA.CALL_CENTER.phone2}
								mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
							/>
							<ChannelPhone value={CHANNELS_CONTACTS.HAPVIDA.CALL_CENTER.phone3} />
						</Stack>
					</ChannelAccordion>
				) : null}
				{isValid && !isResponsibleWithoutBenefits ? (
					<ChannelAccordion
						dataTagId={HelpPageTaggingEnum.CLICK_EXPANDIR_TELEFONE_MARCACAO_AJUDA}
						title={healthExamsAppointments.title}
						handleAccordionToggle={handleAccordionToggle}
						expandedState={expandedState}>
						<Stack gap={2}>
							<AlertInfo.Root>{healthExamsAppointments.alert}</AlertInfo.Root>
							<ChannelRegion label={healthExamsAppointments.region1}>
								<ChannelPhone
									value={
										CHANNELS_CONTACTS.HAPVIDA.APPOINTMENTS_EXAMS.NO_NE.phone1
									}
									mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
								/>
								<ChannelPhone
									value={
										CHANNELS_CONTACTS.HAPVIDA.APPOINTMENTS_EXAMS.NO_NE.phone2
									}
									mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
								/>
								<ChannelPhone
									value={
										CHANNELS_CONTACTS.HAPVIDA.APPOINTMENTS_EXAMS.NO_NE.phone3
									}
								/>
							</ChannelRegion>
							<ChannelRegion label={healthExamsAppointments.region2}>
								<ChannelPhone
									value={
										CHANNELS_CONTACTS.HAPVIDA.APPOINTMENTS_EXAMS.S_SE_CO.phone1
									}
									mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
								/>
								<ChannelPhone
									value={
										CHANNELS_CONTACTS.HAPVIDA.APPOINTMENTS_EXAMS.S_SE_CO.phone2
									}
								/>
							</ChannelRegion>
						</Stack>
					</ChannelAccordion>
				) : null}

				<ChannelAccordion
					dataTagId={HelpPageTaggingEnum.CLICK_EXPANDIR_TELEFONE_SAC_AJUDA}
					title={SAC.title}
					handleAccordionToggle={handleAccordionToggle}
					expandedState={expandedState}>
					<Stack gap={2}>
						<AlertInfo.Root>{SAC.alert}</AlertInfo.Root>
						<ChannelRegion label={SAC.region1}>
							<ChannelPhone value={CHANNELS_CONTACTS.HAPVIDA.SAC.NO_NE.phone1} />
						</ChannelRegion>
						<ChannelRegion label={SAC.region2}>
							<ChannelPhone value={CHANNELS_CONTACTS.HAPVIDA.SAC.S_SE_CO.phone1} />
						</ChannelRegion>
						{!healthOnly ? (
							<ChannelRegion label={SAC.odonto}>
								<ChannelPhone value={CHANNELS_CONTACTS.HAPVIDA.SAC.ODONTO.phone1} />
							</ChannelRegion>
						) : null}
					</Stack>
				</ChannelAccordion>
				<ChannelAccordion
					dataTagId={HelpPageTaggingEnum.CLICK_EXPANDIR_TELEFONE_OUVIDORIA_AJUDA}
					title={ombudsman.title}
					handleAccordionToggle={handleAccordionToggle}
					expandedState={expandedState}>
					<Stack gap={1}>
						<AlertInfo.Root>{ombudsman.alert}</AlertInfo.Root>
						<ChannelPhone
							value={CHANNELS_CONTACTS.HAPVIDA.OMBUDSMAN.phone1}
							mask={CopySectionMaskEnum.PHONE_WITHOUT_DDD}
						/>
						<Box mt={2}>
							<OmbudsmanFormLink
								label={ombudsman.form}
								buttonText={ombudsman.formButton}
								url={CHANNELS_CONTACTS.HAPVIDA.OMBUDSMAN.formUrl}
							/>
						</Box>
					</Stack>
				</ChannelAccordion>
				{!isResponsibleWithoutBenefits ? (
					<Fragment>
						<ChannelAccordion
							dataTagId={
								HelpPageTaggingEnum.CLICK_EXPANDIR_CENTRAL_CANCELAMENTO_SAUDE_AJUDA
							}
							title={cancellation.title}
							handleAccordionToggle={handleAccordionToggle}
							expandedState={expandedState}>
							<ChannelPhone value={CHANNELS_CONTACTS.HAPVIDA.CANCELLATION.phone1} />
						</ChannelAccordion>
						{isValid ? (
							<ChannelAccordion
								dataTagId={
									HelpPageTaggingEnum.CLICK_EXPANDIR_CENTRAL_VENDAS_SAUDE_AJUDA
								}
								title={sales.title}
								handleAccordionToggle={handleAccordionToggle}
								expandedState={expandedState}>
								<ChannelPhone value={CHANNELS_CONTACTS.HAPVIDA.SALES.phone1} />
							</ChannelAccordion>
						) : null}
					</Fragment>
				) : null}
				<FinancialSupportAccordion
					title={financialSupport.title}
					expandedState={expandedState}
					handleAccordionToggle={handleAccordionToggle}
				/>
			</Box>
		</Stack>
	);
}
